"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createInitialState = createInitialState;
exports.createIterableInstance = createIterableInstance;
const pi_syntax_tools_1 = require("@icg360/pi-syntax-tools");
const immer_1 = require("immer");
const stringify_value_1 = require("../utils/stringify-value");
class TermProto {
    [immer_1.immerable] = true;
    key;
    name;
    type;
    format;
    separator;
    default;
    initial;
    ready;
    value;
    input;
    enums;
    label;
    isRelevant = true;
    isRequired = false;
    isReadonly;
    isValid = true;
    validation = [];
    meta;
    get hasValue() {
        return this.value.length > 0;
    }
    get isComplete() {
        return this.isRelevant ? !this.isRequired && this.isValid : true;
    }
    constructor(key, term, existingValue, metaState) {
        this.key = key;
        this.name = term.name;
        this.type = term.valueType || "string";
        this.format = term.format;
        this.separator = term.separator;
        this.default = term.default || "";
        const hasExistingValue = existingValue !== undefined;
        this.input = hasExistingValue ? existingValue : this.default;
        this.initial = (0, stringify_value_1.stringifyValue)(this.input, this);
        this.ready = this.initial;
        this.value = this.initial;
        // The term defaults to readonly if it has a calculate function
        // this can still be overridden by a readonly function
        this.isReadonly = term.calculate !== undefined;
        if (term.meta) {
            this.meta = metaState;
        }
        if (term.enums) {
            this.enums = [];
            for (const { label, value } of term.enums) {
                this.enums.push({
                    label,
                    value,
                    isRelevant: true,
                });
            }
        }
        if (term.label) {
            this.label = term.label.type === "text" ? term.label.value : "";
        }
    }
}
const allowedTermProtoKeys = new Set([
    "key",
    "name",
    "type",
    "format",
    "separator",
    "default",
    "initial",
    "ready",
    "value",
    "input",
    "label",
    "isRelevant",
    "isRequired",
    "isReadonly",
    "isValid",
    "meta",
]);
class IterableProto {
    [immer_1.immerable] = true;
    name;
    terms;
    count;
    instances;
    max;
    constructor(iterable, metaStates, count) {
        this.name = iterable.name;
        this.terms = metaStates;
        this.count = count;
        this.instances = [];
        // The schema requires that max is set, however, we will default max to 1 until it is set
        const max = Number(iterable.max);
        this.max = Number.isNaN(max) || max < 1 ? 1 : max;
    }
    get lists() {
        // We are nesting Proxy objects so we can act like this object already exists
        // iterables.iterableName.lists.terms.termName.property will return an array of
        // the property of the termName for each instance
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return {
            terms: new Proxy({}, {
                get(_, termName) {
                    if (typeof termName !== "string" || !self.terms[termName]) {
                        return undefined;
                    }
                    return new Proxy({}, {
                        get(_, prop) {
                            if (typeof prop !== "string" ||
                                !allowedTermProtoKeys.has(prop)) {
                                return undefined;
                            }
                            const key = prop;
                            return self.instances.map(inst => inst.terms[termName][key]);
                        },
                    });
                },
            }),
        };
    }
}
function createInitialState(modelKey, model, nodeToKey, formMetaState, termValues = {}, envData = {}) {
    const terms = {};
    for (const term of Object.values(model.terms)) {
        const key = nodeToKey.terms[term.name];
        terms[term.name] = new TermProto(key, term, termValues[key], formMetaState.terms[term.name].meta);
    }
    // That was easy, now let's do the iterables
    const iterables = {};
    for (const [iterableName, iterable] of Object.entries(model.iterables)) {
        let count = 0;
        if (iterable.count.type === "syntax-tree") {
            // Let's assume all the data we need to calculate the count is included in the termValues and envData
            count = Number((0, pi_syntax_tools_1.execSyntax)(iterable.count.tree, (0, pi_syntax_tools_1.getContext)({
                key: modelKey,
                phase: "INITIAL",
                env: envData,
                meta: formMetaState.meta,
                terms,
                iterables,
                this: { iterable },
            })));
        }
        if (Number.isNaN(count)) {
            throw new Error(`Invalid count for iterable ${iterableName}`);
        }
        iterables[iterableName] = new IterableProto(iterable, formMetaState.iterables[iterableName].terms, count);
        for (let index = 0; index < count; index += 1) {
            const iterableTerms = {};
            for (const term of Object.values(iterable.terms)) {
                const key = nodeToKey.iterables[iterableName][index]?.[term.name];
                if (!key) {
                    throw new Error(`Could not find key for iterable ${iterableName} term ${term.name}`);
                }
                iterableTerms[term.name] = new TermProto(key, term, termValues[key], formMetaState.iterables[iterableName].terms[term.name].meta);
            }
            iterables[iterableName].instances.push({
                terms: iterableTerms,
            });
        }
    }
    return {
        key: modelKey,
        phase: "INITIAL",
        env: envData,
        terms,
        iterables,
        meta: formMetaState.meta,
    };
}
function createIterableInstance(model, state, nodeToKey, iterableName, index) {
    const iterableTerms = {};
    const iterable = model.iterables[iterableName];
    for (const [termName, term] of Object.entries(iterable.terms)) {
        const key = nodeToKey.iterables[iterableName][index]?.[term.name];
        if (!key) {
            throw new Error(`Could not find key for iterable ${iterableName} term ${term.name}`);
        }
        iterableTerms[termName] = new TermProto(key, term, undefined, state.iterables[iterableName].terms[termName].meta);
    }
    return {
        terms: iterableTerms,
    };
}
//# sourceMappingURL=create-initial-state.js.map